import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatCurrencyAdaptive, formatNumber } from '@/lib/utils';

import type { TopDebtor, TopDebtors } from '../dashboard.api';

interface TopDebtorsPanelProps {
  data: TopDebtors | undefined;
  loading: boolean;
}

/** Cuántos deudores se muestran directamente en la tarjeta. */
const PREVIEW_LIMIT = 4;

/** Fila de un deudor: cliente, RUC/documentos, saldo y barra de peso relativo. */
function DebtorRow({ cliente, maxSaldo }: { cliente: TopDebtor; maxSaldo: number }) {
  return (
    <li>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-ink-800">
            {cliente.razon_social}
          </span>
          <span className="text-[11px] text-ink-400">
            {cliente.ruc ? `RUC ${cliente.ruc} · ` : ''}
            {formatNumber(cliente.documentos_pendientes)}{' '}
            {cliente.documentos_pendientes === 1 ? 'documento' : 'documentos'}
          </span>
        </span>

        <span
          title={formatCurrency(cliente.saldo_pendiente)}
          className="shrink-0 font-mono text-sm font-semibold text-ink-900 tabular-nums"
        >
          {formatCurrencyAdaptive(cliente.saldo_pendiente)}
        </span>
      </div>

      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-ink-100">
        <div
          style={{ width: `${(Number(cliente.saldo_pendiente || 0) / maxSaldo) * 100}%` }}
          className="h-full rounded-full bg-rose-400"
        />
      </div>
    </li>
  );
}

/** Clientes con mayor saldo pendiente de pago. */
export function TopDebtorsPanel({ data, loading }: TopDebtorsPanelProps) {
  const [abierto, setAbierto] = useState(false);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-12 rounded-control" />
        ))}
      </div>
    );
  }

  const detalle = data?.detalle ?? [];

  if (detalle.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-control border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
        <CheckCircle2 className="size-4.5 shrink-0 text-emerald-500" aria-hidden="true" />
        <p>Sin saldos pendientes de clientes: no hay créditos por cobrar.</p>
      </div>
    );
  }

  const maxSaldo = Math.max(...detalle.map((item) => Number(item.saldo_pendiente || 0)), 1);
  const vistaPrevia = detalle.slice(0, PREVIEW_LIMIT);

  return (
    <div className="flex h-full flex-col gap-3">
      <ul className="space-y-3">
        {vistaPrevia.map((cliente) => (
          <DebtorRow
            key={`${cliente.ruc}-${cliente.razon_social}`}
            cliente={cliente}
            maxSaldo={maxSaldo}
          />
        ))}
      </ul>

      {detalle.length > PREVIEW_LIMIT ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAbierto(true)}
          className="self-start"
        >
          Ver todos ({detalle.length})
        </Button>
      ) : null}

      <p className="mt-auto border-t border-ink-100 pt-3 text-xs text-ink-500">
        Total por cobrar:{' '}
        <strong
          title={formatCurrency(data?.saldo_total_por_cobrar ?? 0)}
          className="font-semibold text-ink-800"
        >
          {formatCurrencyAdaptive(data?.saldo_total_por_cobrar ?? 0)}
        </strong>{' '}
        en {formatNumber(detalle.length)} {detalle.length === 1 ? 'cliente' : 'clientes'}
      </p>

      <Dialog
        open={abierto}
        onClose={() => setAbierto(false)}
        title="Principales deudores"
        description={`${detalle.length} clientes con saldo pendiente`}
        size="lg"
      >
        <ul className="space-y-4">
          {detalle.map((cliente) => (
            <DebtorRow
              key={`${cliente.ruc}-${cliente.razon_social}`}
              cliente={cliente}
              maxSaldo={maxSaldo}
            />
          ))}
        </ul>
      </Dialog>
    </div>
  );
}
